import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import Amplitude from 'amplitude-js';
import { useCentralizedProviderData, useMe } from '@crehana/auth';
import { getDeviceType, notEmpty } from '@crehana/utils';
import { initConfig } from "./config";
import { buildCentralizedUserProperties, isValidEventType, showLogs } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
var isTest = process.env.NODE_ENV === 'test';
export var AmplitudeContext = /*#__PURE__*/createContext(undefined);
var AmplitudeProvider = function AmplitudeProvider(_ref) {
  var apiKey = _ref.apiKey,
    _ref$platform = _ref.platform,
    platform = _ref$platform === void 0 ? 'Web' : _ref$platform,
    children = _ref.children,
    _userProp = _ref.user;
  var _useMe = useMe(),
    me = _useMe.me,
    meLoading = _useMe.loading;
  var _useCentralizedProvid = useCentralizedProviderData(),
    centralizedMe = _useCentralizedProvid.me,
    centralizedLoading = _useCentralizedProvid.loading;
  var amplitudeRef = useRef();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    initialized = _useState2[0],
    setInitialized = _useState2[1];
  var eventsQueue = useRef([]);
  var loading = meLoading || centralizedLoading;

  /**
   * The CDN may fail or the user may disable tracking
   */
  var loadingFailed = useRef(false);
  var user = {
    isLoading: (_userProp === null || _userProp === void 0 ? void 0 : _userProp.isLoading) || loading,
    data: (_userProp === null || _userProp === void 0 ? void 0 : _userProp.data) || me,
    centralizedMe: centralizedMe
  };
  useEffect(function () {
    if (apiKey && !amplitudeRef.current) {
      var config = _objectSpread(_objectSpread({}, initConfig), {}, {
        onError: function onError() {
          loadingFailed.current = true;

          // Run all the callbacks if loading failed
          eventsQueue.current.forEach(function (_ref2) {
            var callback = _ref2.callback;
            if (callback) callback();
          });
          eventsQueue.current = [];
        }
      });

      // we don't need to initialize the amplitude client in test mode
      if (isTest) {
        amplitudeRef.current = {
          /* eslint-disable @typescript-eslint/no-empty-function */
          logEvent: function logEvent() {},
          setUserProperties: function setUserProperties() {},
          setUserId: function setUserId() {}
          /* eslint-enable */
        };
        setInitialized(true);
      } else {
        Amplitude.getInstance().init(apiKey, undefined, config, function (instance) {
          amplitudeRef.current = instance;
          setInitialized(true);
        });
      }
    }
  }, [apiKey]);
  useEffect(function () {
    if (initialized && user && !user.isLoading) {
      var userProperties = {};
      userProperties.Platform = platform;
      userProperties['Platform Device'] = getDeviceType();
      var userData = user.data;
      if (user.centralizedMe) {
        Object.assign(userProperties, buildCentralizedUserProperties(user.centralizedMe));
      }
      if (userData !== null && userData !== void 0 && userData.originalId) {
        var _userData$membership, _userData$membership$, _userData$organizatio, _userData$organizatio3, _userData$organizatio4, _userData$organizatio5, _userData$organizatio6, _userData$organizatio7, _userData$organizatio8, _userData$organizatio9, _userData$organizatio0, _userData$organizatio1, _userData$organizatio10, _userData$organizatio11, _userData$organizatio12;
        amplitudeRef.current.setUserId(userData.originalId.toString());
        if (userData.segmentation) {
          userProperties['User Segment'] = userData.segmentation;
        }
        if (userData.segmentationKey !== null && userData.segmentationKey !== undefined) {
          userProperties['User Segment ID'] = userData.segmentationKey;
        }
        if (userData.tastes) {
          var tastes = userData.tastes.filter(notEmpty);
          // Remove repeated categories
          var categories = _toConsumableArray(new Set(tastes.map(function (t) {
            var _t$parent;
            return (_t$parent = t.parent) === null || _t$parent === void 0 ? void 0 : _t$parent.name;
          }).filter(notEmpty)));
          var subCategories = tastes.map(function (t) {
            return t.name;
          });
          if (categories.length) {
            userProperties['Categories Interest'] = categories;
          }
          if (subCategories.length) {
            userProperties['Subcategories Interest'] = subCategories;
          }
        }
        if (userData.occupation) {
          userProperties.Role = userData.occupation;
        }
        if (userData.birthday) {
          userProperties.Birthday = userData.birthday;
        }
        if (userData.goals) {
          var goals = userData.goals.map(function (g) {
            return g === null || g === void 0 ? void 0 : g.name;
          }).filter(notEmpty);
          if (goals.length) {
            userProperties.Objectives = goals;
          }
        }
        if ((_userData$membership = userData.membership) !== null && _userData$membership !== void 0 && (_userData$membership$ = _userData$membership.membershipType) !== null && _userData$membership$ !== void 0 && _userData$membership$.tag) {
          userProperties['Premium Type'] = userData.membership.membershipType.tag;
        }
        if ((_userData$organizatio = userData.organization) !== null && _userData$organizatio !== void 0 && _userData$organizatio.originalId) {
          var _userData$organizatio2;
          userProperties['Organization Id'] = (_userData$organizatio2 = userData.organization) === null || _userData$organizatio2 === void 0 ? void 0 : _userData$organizatio2.originalId;
        }
        if ((_userData$organizatio3 = userData.organization) !== null && _userData$organizatio3 !== void 0 && _userData$organizatio3.slug) {
          userProperties['Organization Slug'] = userData.organization.slug;
        }
        if ((_userData$organizatio4 = userData.organization) !== null && _userData$organizatio4 !== void 0 && _userData$organizatio4.organizationType) {
          userProperties['Organization Type'] = userData.organization.organizationType;
        }
        if ((_userData$organizatio5 = userData.organization) !== null && _userData$organizatio5 !== void 0 && _userData$organizatio5.organizationSize) {
          userProperties['Organization Size'] = userData.organization.organizationSize;
        }
        if ((_userData$organizatio6 = userData.organization) !== null && _userData$organizatio6 !== void 0 && _userData$organizatio6.assignedType) {
          userProperties['Organization Assigned'] = userData.organization.assignedType;
        }
        if ((_userData$organizatio7 = userData.organization) !== null && _userData$organizatio7 !== void 0 && _userData$organizatio7.industry) {
          userProperties['Organization Industry'] = userData.organization.industry;
        }
        if ((_userData$organizatio8 = userData.organization) !== null && _userData$organizatio8 !== void 0 && (_userData$organizatio9 = _userData$organizatio8.configuration) !== null && _userData$organizatio9 !== void 0 && _userData$organizatio9.customerSuccessType) {
          userProperties['Organization Customer Success Type'] = userData.organization.configuration.customerSuccessType;
        }
        if ((_userData$organizatio0 = userData.organization) !== null && _userData$organizatio0 !== void 0 && _userData$organizatio0.startDate) {
          userProperties['Organization Start Date'] = userData.organization.startDate;
        }
        userProperties['Organization Studio Available'] = (_userData$organizatio1 = userData.organization) !== null && _userData$organizatio1 !== void 0 && (_userData$organizatio10 = _userData$organizatio1.configuration) !== null && _userData$organizatio10 !== void 0 && _userData$organizatio10.hasStudioAvailable ? 'Yes' : 'No';
        userProperties['Organization Gamification Available'] = (_userData$organizatio11 = userData.organization) !== null && _userData$organizatio11 !== void 0 && (_userData$organizatio12 = _userData$organizatio11.configuration) !== null && _userData$organizatio12 !== void 0 && _userData$organizatio12.hasGamificationAvailable ? 'Yes' : 'No';
      }
      amplitudeRef.current.setUserProperties(userProperties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, initialized]);
  useEffect(function () {
    if (initialized && eventsQueue.current.length) {
      if (process.env.NODE_ENV === 'development') {
        eventsQueue.current.forEach(function (_ref3) {
          var event = _ref3.event,
            data = _ref3.data;
        });
      }

      // Flush queued events before the api loaded
      eventsQueue.current.forEach(function (logEventConfig) {
        showLogs(logEventConfig);
        amplitudeRef.current.logEvent(logEventConfig.event, logEventConfig.data, logEventConfig.callback);
      });
      eventsQueue.current = [];
    }
  }, [initialized]);
  var logEvent = function logEvent(logEventConfig) {
    if (!isValidEventType(logEventConfig.event)) {
      return;
    }
    if (loadingFailed.current && logEventConfig.callback) {
      logEventConfig.callback();
      return;
    }
    if (!amplitudeRef.current) {
      eventsQueue.current.push(logEventConfig);
      return;
    }
    showLogs(logEventConfig);
    amplitudeRef.current.logEvent(logEventConfig.event, logEventConfig.data, logEventConfig.callback);
  };
  return useMemo(function () {
    return /*#__PURE__*/_jsx(AmplitudeContext.Provider, {
      value: {
        amplitude: amplitudeRef.current,
        logEvent: logEvent
      },
      children: children
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [children, initialized]);
};
export default AmplitudeProvider;